import React, { useRef, useState } from "react"

import { Color } from "../../../../constants/V2/color"
import Typography from "../../../elements/V2/Typography"

import useIsomorphicLayoutEffect from "@hooks/V2/useIsomorphicLayoutEffect"

type Props = {
  plans: string[]
  selectedPlan?: string
  onSelect?: (plan: string) => void
}

type ActiveIndicatorStyle = {
  left: number
  width: number
}

const PlanSelector = ({
  plans,
  selectedPlan: controlledSelectedPlan,
  onSelect,
}: Props) => {
  const [internalSelectedPlan, setInternalSelectedPlan] = useState<string>(
    plans[0]
  )
  const selectedPlan = controlledSelectedPlan ?? internalSelectedPlan
  const selectedPlanIndex = Math.max(0, plans.indexOf(selectedPlan))

  const handleSelect = (plan: string) => {
    if (onSelect) {
      onSelect(plan)
    } else {
      setInternalSelectedPlan(plan)
    }
  }

  const optionElements = useRef<Array<HTMLDivElement | null>>([])
  const [activeIndicatorStyle, setActiveIndicatorStyle] =
    useState<ActiveIndicatorStyle | null>(null)

  useIsomorphicLayoutEffect(() => {
    const selectedOptionElement = optionElements.current[selectedPlanIndex]
    if (!selectedOptionElement) return

    setActiveIndicatorStyle({
      left: selectedOptionElement.offsetLeft,
      width: selectedOptionElement.offsetWidth,
    })
  }, [selectedPlanIndex, plans])

  return (
    <div className="relative flex h-48 w-fit gap-4 rounded-32 bg-white p-6">
      {activeIndicatorStyle && (
        <div
          aria-hidden
          className="absolute bottom-6 left-0 top-6 flex items-center justify-start rounded-32 border border-gray bg-light-gray pl-10 transition-[transform,width] duration-300 ease-out will-change-transform"
          style={{
            transform: `translateX(${activeIndicatorStyle.left}px)`,
            width: `${activeIndicatorStyle.width}px`,
          }}
        >
          <div className="mb-[2px] h-8 w-8 rounded-full bg-blue" />
        </div>
      )}
      {plans.map((plan, planIndex) => (
        <div
          key={plan}
          ref={(node) => {
            optionElements.current[planIndex] = node
          }}
          className="relative z-10 flex h-full cursor-pointer items-center justify-center gap-8 px-10"
          onClick={() => handleSelect(plan)}
        >
          {selectedPlan === plan && <div aria-hidden className="h-8 w-8" />}
          <Typography
            text={plan}
            font="grotesk"
            weight="book"
            color={Color.Black}
            size="body-md"
          />
        </div>
      ))}
    </div>
  )
}

export default PlanSelector
